/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { css } from 'aphrodite';

import { colors, LoadingSpinner } from '@blend/consumer-components';

import { templateTypeMap } from '../form/config';
import { TaskMessages } from '../form/TaskMessages/TaskMessages';
import { ConsumerAppConfigContext } from '../../contexts/appConfig';
import { styles as taskStyles } from '../form/styles';
import { useConsumerTheme } from '../../hooks/useConsumerTheme';
import { TaskForm } from '../form/TaskForm';
import { ContextualHelpList } from '../form/ContextualHelpList/ContextualHelpList';
import { FORM_NAME } from '../form/ConnectedTaskForm';
import { participantAPI } from '../../api/participant';
import { ExitWindowButton } from '../common/ExitWindowButton/ExitWindowButton';
import type { RoomsAPI } from '../../api/participant/roomsClient';
import { DEVICE_SETUP_TEMPLATE } from './deviceSetupTemplate';

export namespace StandaloneDeviceTesting {
  export interface Props {}
  export interface OwnProps {
    handleSubmit: (values: any) => void;
  }
}

const generateStyles = (_appConfig: ConsumerAppConfigContext) => taskStyles;

const generateDeviceSetupTemplate = (deviceConfig: RoomsAPI.DeviceTestingConfigResponse) => {
  return templateTypeMap.deviceSetup(DEVICE_SETUP_TEMPLATE.inputs, {
    task: {
      template: {
        ...DEVICE_SETUP_TEMPLATE,
        customProps: {
          videoAuthToken: deviceConfig.videoAuthToken,
          videoProvider: deviceConfig.videoProvider,
          testSessionId: deviceConfig.testSessionId,
        },
      },
    },
  });
};

export const _StandaloneDeviceTesting: React.FC<StandaloneDeviceTesting.Props> = () => {
  const { styles } = useConsumerTheme(generateStyles);
  const { roomId } = useParams<{ roomId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceConfig, setDeviceConfig] = useState<RoomsAPI.DeviceTestingConfigResponse | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!roomId) {
        setError('No room ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const config = await participantAPI.rooms.fetchDeviceTestingConfig(roomId);
        setDeviceConfig(config);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load device testing configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [roomId]);

  let content;

  if (loading) {
    content = (
      <div className={css(styles.defaultTemplate)}>
        <TaskMessages messages={DEVICE_SETUP_TEMPLATE.messages} />
        <div style={{ textAlign: 'center' }} data-testid="loading-spinner">
          <LoadingSpinner size="lg" color={colors.catalyst} />
        </div>
      </div>
    );
  } else if (error || !deviceConfig) {
    content = (
      <div className={css(styles.defaultTemplate)}>
        <div>Error: {error || 'Failed to load configuration'}</div>
        <ExitWindowButton />
      </div>
    );
  } else {
    content = (
      <div className={css(styles.defaultTemplate)}>
        <div>
          <TaskMessages messages={DEVICE_SETUP_TEMPLATE.messages} />
          {generateDeviceSetupTemplate(deviceConfig)}
          <ContextualHelpList contextualHelps={DEVICE_SETUP_TEMPLATE.contextualHelps} />
          <ExitWindowButton />
          <br />
        </div>
      </div>
    );
  }

  // This onSubmit needs to be re-created every time, otherwise we will be submitting the old task
  return <TaskForm onSubmit={() => {}} formTemplate={content} />;
};

export const StandaloneDeviceTesting = connect()(
  reduxForm<{}, StandaloneDeviceTesting.Props>({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })(_StandaloneDeviceTesting),
);
